import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Seo from '@/components/base/Seo';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getArticle, articles, siteInfo } from '@/mocks/anime';

export default function ArticleDetail() {
  const { id } = useParams();
  const article = id ? getArticle(id) : undefined;
  const [open, setOpen] = useState(0);

  if (!article) {
    return (
      <Layout>
        <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 pt-24 text-center">
          <i className="ri-file-shield-2-line text-5xl text-foreground-300" aria-hidden="true" />
          <h1 className="mt-4 font-heading text-2xl text-foreground-950">文章不存在或已下线</h1>
          <Link to="/articles" className="mt-5 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors">
            返回新番资讯
          </Link>
        </div>
      </Layout>
    );
  }

  const others = articles.filter((n) => n.id !== article.id).slice(0, 3);
  const url = `${siteInfo.url}/article/${article.id}`;

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.summary,
      author: { '@type': 'Person', name: article.author },
      datePublished: article.date,
      dateModified: article.date,
      image: article.image,
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      publisher: { '@type': 'Organization', name: siteInfo.name, url: siteInfo.url },
      articleSection: article.categoryName,
      inLanguage: 'zh-CN',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
        { '@type': 'ListItem', position: 2, name: '新番资讯', item: `${siteInfo.url}/articles` },
        { '@type': 'ListItem', position: 3, name: article.title, item: url },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: article.faq.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  return (
    <Layout>
      <Seo title={`${article.title} - 动漫网`} description={article.summary} path={`/article/${article.id}`} jsonLd={jsonLd} />

      <div className="bg-gradient-to-br from-primary-100 via-background-100 to-accent-100 pt-24 pb-6">
        <div className="mx-auto w-full max-w-3xl px-4 md:px-6">
          <Breadcrumb
            items={[
              { name: '首页', path: '/' },
              { name: '新番资讯', path: '/articles' },
              { name: article.title },
            ]}
          />
          <h1 className="mt-3 font-heading text-2xl leading-snug text-foreground-950 md:text-3xl">{article.title}</h1>
          <p className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5"><i className="ri-user-line" aria-hidden="true" />{article.author}</span>
            <span className="flex items-center gap-1.5"><i className="ri-calendar-line" aria-hidden="true" />{article.date}</span>
            <span className="flex items-center gap-1.5"><i className="ri-time-line" aria-hidden="true" />{article.readTime}</span>
            <span className="flex items-center gap-1.5"><i className="ri-eye-line" aria-hidden="true" />{article.views} 阅读</span>
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-3xl px-4 md:px-6 py-8">
        <div className="overflow-hidden rounded-lg">
          <img src={article.image} alt={article.title} title={`${article.title} - 动漫网`} className="w-full object-cover" />
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {article.tags.map((t) => (
            <span key={t} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">{t}</span>
          ))}
        </div>

        <article className="mt-6 space-y-8">
          {article.sections.map((s, i) => (
            <section key={i}>
              <h2 className="flex items-center gap-2 font-heading text-xl text-foreground-950">
                <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-500 text-sm text-background-50">
                  {i + 1}
                </span>
                {s.heading}
              </h2>
              <p className="mt-3 text-base leading-relaxed text-foreground-700">{s.body}</p>
            </section>
          ))}
        </article>

        <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-background-200 pt-6">
          <span className="text-sm text-foreground-600">分享到：</span>
          {[
            { icon: 'ri-weibo-fill', label: '微博' },
            { icon: 'ri-bilibili-fill', label: '哔哩哔哩' },
            { icon: 'ri-twitter-x-fill', label: 'X' },
          ].map((s) => (
            <a key={s.label} href="#" aria-label={`分享到${s.label}`} className="flex h-9 w-9 items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-500 hover:text-background-50 transition-colors">
              <i className={s.icon} aria-hidden="true" />
            </a>
          ))}
          <button
            type="button"
            onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="ml-auto flex items-center gap-1.5 rounded-full bg-background-100 px-4 py-2 text-sm text-foreground-700 hover:bg-background-200 transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-up-line" aria-hidden="true" />返回顶部
          </button>
        </div>

        <section className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
          <h2 className="font-heading text-xl text-foreground-950">常见问题解答</h2>
          <div className="mt-4 space-y-3">
            {article.faq.map((f, i) => (
              <div key={i} className="overflow-hidden rounded-lg bg-background-50">
                <button type="button" onClick={() => setOpen(open === i ? -1 : i)} aria-expanded={open === i} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left">
                  <span className="font-medium text-foreground-900">{f.q}</span>
                  <i className={`${open === i ? 'ri-subtract-line' : 'ri-add-line'} text-foreground-500`} aria-hidden="true" />
                </button>
                {open === i && <p className="px-4 pb-4 text-sm text-foreground-600">{f.a}</p>}
              </div>
            ))}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="font-heading text-xl text-foreground-950">相关资讯</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {others.map((n) => (
              <Link key={n.id} to={`/article/${n.id}`} className="group overflow-hidden rounded-lg bg-background-100">
                <div className="aspect-[16/10] w-full overflow-hidden">
                  <img src={n.image} alt={n.title} title={`${n.title} - 动漫网`} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <h3 className="p-4 text-sm font-medium leading-snug text-foreground-900 group-hover:text-primary-600 transition-colors">{n.title}</h3>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </Layout>
  );
}